/**
 * AssetField - drag/drop zone for importing asset files (images, videos, audio, models)
 */

import React, { useState, useEffect, useRef } from 'react';
import type { ResourceManager } from 'trace-viewer/scene';
import {
    IMAGE_EXTENSIONS, VIDEO_EXTENSIONS, AUDIO_EXTENSIONS, MODEL_EXTENSIONS,
    toAcceptString,
} from 'trace-viewer/scene';

export interface AssetFieldProps {
    label: string;
    value: string;
    onChange: (value: string) => void;
    assetType?: 'image' | 'video' | 'audio' | 'model';
    resources?: ResourceManager;
    description?: string;
    /** Override label min-width (default 100px) */
    labelWidth?: string;
}

const EXTENSION_SETS: Record<string, Set<string>> = {
    image: IMAGE_EXTENSIONS,
    video: VIDEO_EXTENSIONS,
    audio: AUDIO_EXTENSIONS,
    model: MODEL_EXTENSIONS,
};

const ASSET_ACCEPT: Record<string, string> = {
    image: toAcceptString(IMAGE_EXTENSIONS),
    video: toAcceptString(VIDEO_EXTENSIONS),
    audio: toAcceptString(AUDIO_EXTENSIONS),
    model: toAcceptString(MODEL_EXTENSIONS),
};

const ASSET_LABELS: Record<string, string> = {
    image: 'Drop image or click to browse',
    video: 'Drop video or click to browse',
    audio: 'Drop audio or click to browse',
    model: 'Drop GLB file or click to browse',
};

export const AssetField: React.FC<AssetFieldProps> = ({ label, value, onChange, assetType = 'image', resources, description, labelWidth }) => {
    const [isDragging, setIsDragging] = useState(false);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const dragCounterRef = useRef(0);

    useEffect(() => {
        if (value && resources) {
            const blob = resources.getAsset(value);
            if (blob) {
                const url = URL.createObjectURL(blob);
                setPreviewUrl(url);
                return () => URL.revokeObjectURL(url);
            }
        }
        setPreviewUrl(null);
    }, [value, resources]);

    useEffect(() => {
        const reset = () => {
            dragCounterRef.current = 0;
            setIsDragging(false);
        };
        document.addEventListener('drop', reset);
        document.addEventListener('dragend', reset);
        return () => {
            document.removeEventListener('drop', reset);
            document.removeEventListener('dragend', reset);
        };
    }, []);

    const isValidFile = (file: File): boolean => {
        const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
        return EXTENSION_SETS[assetType]?.has(ext) ?? false;
    };

    const handleFile = async (file: File) => {
        if (!isValidFile(file) || !resources) return;
        const assetKey = `assets/${file.name}`;
        const storedPath = await resources.addAssetWithDedup(assetKey, file);
        onChange(`./${storedPath}`);
    };

    const displayValue = value ? value.replace(/^\.\/assets\//, '').replace(/^assets\//, '') : '';

    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }} title={description}>
            <span style={{ fontSize: '12px', color: '#aaa', minWidth: labelWidth ?? '100px', flexShrink: 0 }}>{label}</span>
            <div
                onClick={() => fileInputRef.current?.click()}
                onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); dragCounterRef.current++; if (e.dataTransfer?.types.includes('Files')) setIsDragging(true); }}
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); dragCounterRef.current--; if (dragCounterRef.current === 0) setIsDragging(false); }}
                onDrop={async (e) => {
                    e.preventDefault(); e.stopPropagation();
                    dragCounterRef.current = 0; setIsDragging(false);
                    const file = Array.from(e.dataTransfer?.files || []).find(isValidFile);
                    if (file) await handleFile(file);
                }}
                style={{
                    flex: 1, display: 'flex', alignItems: 'center', gap: '8px',
                    padding: '4px 8px',
                    backgroundColor: isDragging ? '#3a4a3a' : '#333',
                    border: `1px ${isDragging ? 'dashed' : 'solid'} ${isDragging ? '#4CAF50' : '#555'}`,
                    borderRadius: '3px', cursor: 'pointer',
                    transition: 'all 0.15s',
                }}
            >
                {assetType === 'image' && previewUrl && (
                    <div style={{
                        width: '24px', height: '24px', borderRadius: '2px',
                        backgroundImage: `url(${previewUrl})`, backgroundSize: 'cover',
                        backgroundPosition: 'center', flexShrink: 0,
                    }} />
                )}
                <span style={{
                    flex: 1, fontSize: '12px',
                    color: value ? '#fff' : '#888',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                    {value ? displayValue : ASSET_LABELS[assetType]}
                </span>
                {value && (
                    <button
                        onClick={(e) => { e.stopPropagation(); onChange(''); }}
                        style={{
                            padding: '2px 4px', backgroundColor: 'transparent',
                            border: 'none', color: '#888', cursor: 'pointer',
                            fontSize: '12px', lineHeight: 1,
                        }}
                        title="Clear"
                    >
                        &times;
                    </button>
                )}
            </div>
            <input
                ref={fileInputRef}
                type="file"
                accept={ASSET_ACCEPT[assetType]}
                onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (file) await handleFile(file);
                    e.target.value = '';
                }}
                style={{ display: 'none' }}
            />
        </div>
    );
};
